import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import type { SortingState } from "@tanstack/react-table"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date
  return d.toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
}

export function formatTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date
  return d.toLocaleTimeString("pl-PL", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
}

export function formatDateTime(date: Date | string): string {
  return `${formatDate(date)}, ${formatTime(date)}`
}

export function formatCurrency(
  amount: number,
  currency: string = "PLN"
): string {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * Convert TanStack Table SortingState to the API sort parameter format.
 * e.g. [{id: "name", desc: false}] => "name"
 *      [{id: "created_at", desc: true}] => "-created_at"
 */
export function sortingStateToParam(sorting: SortingState): string | undefined {
  if (sorting.length === 0) return undefined
  return sorting
    .map((s) => (s.desc ? `-${s.id}` : s.id))
    .join(",")
}

/**
 * Build a detail page URL that preserves current list context (sort + filters).
 * The detail page's RecordNavigation component reads these params to fetch
 * prev/next neighbors in the same order.
 */
export function buildDetailUrl(
  basePath: string,
  id: string,
  context: {
    sort?: string
    filters?: Record<string, string | undefined>
  }
): string {
  const params = new URLSearchParams()
  if (context.sort) {
    params.set("sort", context.sort)
  }
  if (context.filters) {
    for (const [key, value] of Object.entries(context.filters)) {
      if (value) {
        params.set(key, value)
      }
    }
  }
  const qs = params.toString()
  return `${basePath}/${id}${qs ? `?${qs}` : ""}`
}
